import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { AlertTriangle, ShieldCheck, Globe, Calendar, FileSearch, ArrowRight, Info } from "lucide-react";
import TrustScoreRing from "../components/TrustScoreRing";
import ProSection from "../components/ProSection";
import BadgeBuilder from "../components/BadgeBuilder";
import ManualUrlsBanner from "../components/ManualUrlsBanner";
import { getReport, API } from "../lib/api";

const CATEGORY_LABELS = {
  privacy_policy: "Privacy Policy",
  terms: "Terms of Service",
  cookies: "Cookies & Consent",
  trackers: "Third-Party Trackers",
  gdpr: "GDPR Readiness",
  payments: "Payments & Billing",
};

export default function Report() {
  const { domain } = useParams();
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const reload = async () => {
    try {
      const data = await getReport(domain);
      setReport(data);
    } catch (e) {
      setError(e?.response?.data?.detail || "Report not found");
    }
  };

  useEffect(() => {
    getReport(domain)
      .then(setReport)
      .catch((e) => setError(e?.response?.data?.detail || "Report not found"))
      .finally(() => setLoading(false));
  }, [domain]);

  if (loading) {
    return (
      <main className="border-b border-black" data-testid="report-loading">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-12 py-24 text-center">
          <div className="font-mono text-sm uppercase tracking-widest text-[#475569]">
            Loading report…
          </div>
        </div>
      </main>
    );
  }

  if (error) {
    return (
      <main className="border-b border-black" data-testid="report-error">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-12 py-24 text-center">
          <h1 className="font-display text-4xl tracking-tight">No report for {domain}</h1>
          <p className="text-[#475569] mt-3">{error}</p>
          <Link to="/" className="inline-block mt-6 pl-btn-primary px-6 py-3 border border-black" data-testid="report-error-scan-cta">
            Scan {domain}
          </Link>
        </div>
      </main>
    );
  }

  const score = report.trust_score || 0;
  const issues = report.issues || [];
  const breakdown = report.breakdown || {};
  const trackers = report.trackers_found || [];
  const created = report.created_at ? new Date(report.created_at) : null;

  return (
    <main className="border-b border-black" data-testid="report-page">
      {/* Header */}
      <section className="border-b border-black bg-white">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-12 py-12">
          <div className="grid lg:grid-cols-12 gap-10">
            <div className="lg:col-span-7">
              <div className="font-mono text-[11px] uppercase tracking-widest text-[#0047FF]">
                / Public TrustScore Report
              </div>
              <h1 className="font-display text-5xl md:text-6xl lg:text-7xl tracking-tight mt-3 leading-[0.95]" data-testid="report-domain-heading">
                {report.domain}
              </h1>
              {report.is_verified && (
                <div
                  className="mt-4 inline-flex items-center gap-2 border border-[#10B981] bg-[#F0FDF4] px-3 py-1.5 text-sm"
                  title={report.pinned_reason || "Verified by PrivacyLens"}
                  data-testid="report-verified-badge"
                >
                  <span className="font-mono text-[10px] uppercase tracking-widest text-[#10B981]">
                    ✓ Verified by PrivacyLens
                  </span>
                  {report.pinned_at && (
                    <span className="text-xs text-[#475569] font-mono">
                      {new Date(report.pinned_at).toLocaleDateString()}
                    </span>
                  )}
                </div>
              )}
              <p className="text-[#475569] mt-4 max-w-2xl leading-relaxed" data-testid="report-summary">
                {report.summary}
              </p>

              <div className="flex flex-wrap gap-4 mt-6 text-sm font-mono uppercase tracking-widest text-[#475569]">
                <span className="flex items-center gap-2">
                  <Globe className="w-4 h-4" />
                  <a href={report.url} target="_blank" rel="noreferrer" className="underline underline-offset-2 hover:text-[#0047FF]" data-testid="report-url-link">
                    {report.url}
                  </a>
                </span>
                {created && (
                  <span className="flex items-center gap-2">
                    <Calendar className="w-4 h-4" />
                    {created.toLocaleDateString()} {created.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}
                  </span>
                )}
                <span className="flex items-center gap-2">
                  <FileSearch className="w-4 h-4" />
                  {(report.pages_analyzed || []).length} pages analyzed
                </span>
                {(report.spa_rendered_pages || []).length > 0 && (
                  <span
                    className="flex items-center gap-2 text-[#0047FF]"
                    title={`These pages required JavaScript rendering: ${report.spa_rendered_pages.join(", ")}`}
                    data-testid="spa-rendered-badge"
                  >
                    <FileSearch className="w-4 h-4" />
                    {report.spa_rendered_pages.length} JS-rendered
                  </span>
                )}
                {report.sitemap_used && (
                  <span
                    className="flex items-center gap-2 text-[#10B981]"
                    title="Found policy URLs via /sitemap.xml"
                    data-testid="sitemap-used-badge"
                  >
                    <FileSearch className="w-4 h-4" />
                    Sitemap used
                  </span>
                )}
              </div>
            </div>

            <div className="lg:col-span-5 flex justify-center lg:justify-end items-center">
              <div className="border border-black bg-white p-8 pl-hard-shadow">
                <TrustScoreRing score={score} size={240} />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Manual-URLs banner — appears when auto-discovery missed a doc */}
      <ManualUrlsBanner report={report} onUpdate={reload} />

      {/* Breakdown */}
      <section className="border-b border-black bg-[#F8F9FA]">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-12 py-12">
          <div className="font-mono text-[11px] uppercase tracking-widest text-[#475569] mb-3">
            Category breakdown
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-px bg-[#E2E8F0] border border-[#E2E8F0]" data-testid="report-breakdown">
            {Object.entries(CATEGORY_LABELS).map(([key, label]) => {
              const v = breakdown[key] ?? 0;
              const c = v >= 80 ? "#10B981" : v >= 60 ? "#F59E0B" : "#EF4444";
              return (
                <div key={key} className="bg-white p-5" data-testid={`breakdown-${key}`}>
                  <div className="font-mono text-[10px] uppercase tracking-widest text-[#475569]">
                    {label}
                  </div>
                  <div className="flex items-end gap-2 mt-2">
                    <span className="font-display text-4xl leading-none" style={{ color: c }}>
                      {v}
                    </span>
                    <span className="text-[11px] font-mono text-[#475569] pb-1">/100</span>
                  </div>
                  <div className="h-1.5 bg-[#F1F5F9] mt-3">
                    <div className="h-full" style={{ width: `${v}%`, background: c }} />
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Projected score teaser — pulls free-tier visitors toward Pro */}
      {report.projected_score && !report.pro_unlocked && report.projected_score > (report.trust_score || 0) && (
        <section className="border-b border-black bg-[#0A0A0A] text-white" data-testid="projected-score-teaser">
          <div className="max-w-[1400px] mx-auto px-6 lg:px-12 py-12 grid lg:grid-cols-12 gap-8 items-center">
            <div className="lg:col-span-7">
              <div className="font-mono text-[11px] uppercase tracking-widest text-[#0047FF]">
                / Your projected TrustScore
              </div>
              <h2 className="font-display text-4xl md:text-5xl tracking-tight leading-[1.02] mt-3">
                Lift to{" "}
                <span className="bg-[#10B981] text-black px-2">{report.projected_score}</span>{" "}
                /100 with PrivacyLens Pro.
              </h2>
              <p className="text-white/70 mt-4 max-w-xl leading-relaxed">
                {report.projected_delta_explanation ||
                  `Adopting the AI-drafted Privacy, Terms and Cookie policies and fixing every flagged issue typically lifts ${report.domain}'s score by ${report.projected_score - (report.trust_score || 0)} points.`}
              </p>
              <p className="text-[11px] font-mono uppercase tracking-widest text-white/40 mt-4">
                One-time $10 · 3 tailored PDFs · Yours forever
              </p>
            </div>
            <div className="lg:col-span-5">
              <div className="flex items-end gap-3 justify-center">
                <div className="text-center">
                  <div className="font-mono text-[10px] uppercase tracking-widest text-white/50">Now</div>
                  <div className="font-display text-6xl tracking-tight text-white/40">
                    {report.trust_score}
                  </div>
                </div>
                <ArrowRight className="w-10 h-10 text-[#0047FF] mb-3" />
                <div className="text-center">
                  <div className="font-mono text-[10px] uppercase tracking-widest text-[#10B981]">After adoption</div>
                  <div className="font-display text-7xl tracking-tight text-[#10B981]">
                    {report.projected_score}
                  </div>
                </div>
              </div>
              <a
                href="#pro-section"
                className="mt-6 w-full pl-btn-primary px-4 py-3 border border-white inline-flex items-center justify-center gap-2 text-sm"
                data-testid="projected-score-cta"
              >
                Unlock my projected score <ArrowRight className="w-4 h-4" />
              </a>
            </div>
          </div>
        </section>
      )}

      {/* Issues */}
      <section className="border-b border-black">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-12 py-14">
          <div className="font-mono text-[11px] uppercase tracking-widest text-[#0047FF] mb-3">
            / Issues found
          </div>
          <h2 className="font-display text-4xl md:text-5xl tracking-tight">
            {issues.length} potential issue{issues.length === 1 ? "" : "s"}
          </h2>

          <div className="mt-8 border border-[#E2E8F0]" data-testid="issues-list">
            {issues.length === 0 ? (
              <div className="p-12 text-center text-[#475569]">
                <ShieldCheck className="w-10 h-10 mx-auto text-[#10B981]" />
                <div className="mt-3 font-mono uppercase tracking-widest text-sm">
                  No major issues detected
                </div>
              </div>
            ) : (
              issues.map((iss, idx) => (
                <div key={idx} className="border-b border-[#E2E8F0] last:border-b-0 p-5 lg:p-6 grid md:grid-cols-12 gap-4" data-testid={`issue-${idx}`}>
                  <div className="md:col-span-2 flex md:flex-col items-start gap-2">
                    <span className={`font-mono text-[10px] uppercase tracking-widest px-1.5 py-0.5 border sev-${iss.severity}`}>
                      {iss.severity}
                    </span>
                    <span className="font-mono text-[10px] uppercase tracking-widest text-[#475569]">
                      {iss.category}
                    </span>
                  </div>
                  <div className="md:col-span-10">
                    <h3 className="font-display text-lg md:text-xl tracking-tight">{iss.title}</h3>
                    <p className="text-sm text-[#475569] mt-1.5 leading-relaxed">{iss.description}</p>
                    {iss.recommendation && (
                      <div className="mt-3 border-l-2 border-[#0047FF] pl-3 text-sm">
                        <span className="font-mono text-[10px] uppercase tracking-widest text-[#0047FF]">
                          Recommendation
                        </span>
                        <p className="text-[#0A0A0A] mt-0.5">{iss.recommendation}</p>
                      </div>
                    )}
                  </div>
                </div>
              ))
            )}
          </div>
        </div>
      </section>

      {/* Trackers detected */}
      {trackers.length > 0 && (
        <section className="border-b border-black bg-[#F8F9FA]">
          <div className="max-w-[1400px] mx-auto px-6 lg:px-12 py-12">
            <div className="font-mono text-[11px] uppercase tracking-widest text-[#475569] mb-3">
              Trackers detected on homepage
            </div>
            <div className="flex flex-wrap gap-2" data-testid="trackers-list">
              {trackers.map((t) => (
                <span
                  key={t}
                  className="font-mono text-xs uppercase tracking-widest bg-black text-white px-2.5 py-1.5"
                >
                  {t}
                </span>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* PRO SECTION */}
      <ProSection
        report={report}
        onUpdate={(deep, pro, extra) =>
          setReport((r) => ({
            ...r,
            ...(deep ? { deep_scan: deep } : {}),
            ...(pro ? { pro_documents: pro } : {}),
            ...(extra || {}),
          }))
        }
      />

      {/* RiskBadge Studio — embeddable widget builder */}
      <BadgeBuilder report={report} />

      {/* Disclaimer + take-down */}
      <section className="border-b border-black">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-12 py-10 space-y-4">
          <div
            className="flex items-start gap-4 border border-[#E2E8F0] p-5 bg-[#F8F9FA]"
            data-testid="report-disclaimer"
          >
            <AlertTriangle className="w-5 h-5 text-[#F59E0B] flex-shrink-0 mt-0.5" />
            <p className="text-sm text-[#475569] leading-relaxed">
              <strong className="text-black">PrivacyLens is not a law firm and does not provide legal advice.</strong>{" "}
              This report and all scores, recommendations, and generated documents for{" "}
              <span className="font-mono text-black">{report.domain}</span> are AI-generated
              assessments based on publicly available information and automated analysis. Results
              may be incomplete, inaccurate, or may not reflect this organization&apos;s full
              compliance posture.{" "}
              <strong className="text-black">Have a qualified attorney review before taking action or publishing any AI-drafted policy.</strong>{" "}
              Use of PrivacyLens does not create an attorney&ndash;client relationship.
            </p>
          </div>
          <div
            className="flex items-start gap-4 border border-[#E2E8F0] p-5 bg-white text-xs text-[#475569] leading-relaxed"
            data-testid="report-takedown"
          >
            <span className="font-mono text-[10px] uppercase tracking-widest text-[#475569] mt-0.5 flex-shrink-0">
              / Domain owner?
            </span>
            <p>
              If you own <span className="font-mono text-black">{report.domain}</span> and
              believe this report is inaccurate or should be removed, submit a{" "}
              <a
                href={`mailto:support@getprivacylens.com?subject=${encodeURIComponent("Take-down / inaccuracy report for " + report.domain)}&body=${encodeURIComponent("Domain: " + report.domain + "\nReport URL: " + (typeof window !== "undefined" ? window.location.href : "") + "\n\nPlease describe the inaccuracy or reason for take-down:\n")}`}
                className="text-[#0047FF] hover:underline font-medium"
                data-testid="report-inaccuracy-link"
              >
                Report inaccuracy / Request take-down →
              </a>
              {" "}— we honour verified requests within 5 business days. Verification methods and the full procedure are documented in our{" "}
              <a href="/legal/privacy.html#takedown" className="text-[#0047FF] hover:underline">
                Privacy Policy §8c
              </a>.
            </p>
          </div>
          <div
            className="flex items-start gap-4 border border-[#0047FF] p-5 bg-[#0047FF]/5 text-xs text-[#0A0A0A] leading-relaxed"
            data-testid="report-public-info"
          >
            <Info className="w-5 h-5 text-[#0047FF] flex-shrink-0 mt-0.5" />
            <p>
              <strong>This report is public on purpose.</strong> Anyone — consumers, regulators, journalists,
              or your prospective customers — can read PrivacyLens reports without an account to decide whether
              a website is trustworthy before signing up or sharing data with it.
              It&apos;s the same accountability model used by the credit-reporting and food-hygiene industries.
              The TrustScore, breakdown and findings are visible to everyone; AI-drafted policy PDFs are only available to the verified buyer.
            </p>
          </div>
        </div>
      </section>
    </main>
  );
}
